import { useEffect, useMemo, useState } from 'react';
import { Logo, Field } from '../components/ui/bits.jsx';
import { useUI } from '../components/ui/feedback.jsx';
import { saveProfileForm, loadAktivneSole, solaPoKodi } from '../lib/api.js';
import { FIELDS, EMPTY_PROFILE, validateProfile } from '../lib/profileFields.js';
import { Icon } from '../components/ui/Icon.jsx';
import { Zasebnost } from './Zasebnost.jsx';
import { VIRI } from '../lib/viri.js';
import { PERUT_SOLA_ID } from '../config.js';
import { t } from '../i18n/index.js';
import { UstvariSolo } from './UstvariSolo.jsx';

/*
  Vpis podatkov ob prvem obisku.

  Novo: tečajnik najprej izbere ŠOLO. Šolo določi povezava (?sola=slug),
  koda šole ali izbira s seznama šol, ki sprejemajo tečajnike. Dokler je
  šola ena sama, izbire ni — izbere se sama.

  - obrazec je <form>, zato Enter deluje in brskalnik ponudi samodejno
    izpolnjevanje (autoComplete),
  - napake se pokažejo pri polju, ne kot alert,
  - obvezna polja so označena in preverjena pred pošiljanjem.
*/

const SLUG_KEY = 'perut-sola-slug';

function slugIzUrl() {
  try {
    const u = new URLSearchParams(window.location.search);
    return u.get('sola') || u.get('skola') || null;
  } catch {
    return null;
  }
}

export function Onboarding({ session, onDone, onSignOut }) {
  const { toast } = useUI();
  const [form, setForm] = useState({ ...EMPTY_PROFILE, priimek: '', izjava_sprejeta: false, vir_prihoda: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [zasebnost, setZasebnost] = useState(false);

  const [sole, setSole] = useState(null); // null = se nalaga
  // 'tecajnik' = vpis v šolo, 'sola' = inštruktor ustvari svojo šolo.
  const [nacin, setNacin] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('nova-sola') === '1' ? 'sola' : 'tecajnik';
    } catch {
      return 'tecajnik';
    }
  });
  const [solaId, setSolaId] = useState(null);
  const [koda, setKoda] = useState('');
  const [kodaBusy, setKodaBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadAktivneSole()
      .then((list) => {
        if (cancelled) return;
        setSole(list);
        const slug = slugIzUrl() ?? safeGet(SLUG_KEY);
        const bySlug = list.find((s) => s.slug === slug);
        if (bySlug) setSolaId(bySlug.id);
        else if (list.length === 1) setSolaId(list[0].id);
        else if (list.length === 0) setSolaId(PERUT_SOLA_ID);
      })
      .catch(() => {
        if (cancelled) return;
        // Stara baza brez šol: vsi gredo v Perut.
        setSole([]);
        setSolaId(PERUT_SOLA_ID);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sola = useMemo(() => (sole ?? []).find((s) => s.id === solaId) ?? null, [sole, solaId]);

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors((e) => {
      const next = { ...e };
      Object.keys(patch).forEach((k) => delete next[k]);
      return next;
    });
  };

  const potrdiKodo = async () => {
    if (!koda.trim()) return;
    setKodaBusy(true);
    try {
      const s = await solaPoKodi(koda);
      if (!s) {
        setErrors((e) => ({ ...e, sola: t('Šola s to kodo ne obstaja.') }));
        return;
      }
      setSole((l) => (l?.some((x) => x.id === s.id) ? l : [...(l ?? []), s]));
      setSolaId(s.id);
      setErrors((e) => ({ ...e, sola: undefined }));
      safeSet(SLUG_KEY, s.slug);
    } catch (e) {
      toast.bad(e.message ?? 'Kode ni bilo mogoče preveriti.');
    } finally {
      setKodaBusy(false);
    }
  };

  const validate = () => {
    const e = validateProfile(form);
    if (!solaId) e.sola = 'Izberi šolo.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      const first = document.querySelector('[aria-invalid="true"]');
      first?.focus();
      first?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    setSaving(true);
    try {
      await saveProfileForm(session.user.id, session.user.email, form, solaId);
      if (sola?.slug) safeSet(SLUG_KEY, sola.slug);
      toast.ok('Podatki so shranjeni. Dobrodošel!');
      await onDone();
    } catch (err) {
      toast.bad(err.message ?? 'Shranjevanje ni uspelo.');
    } finally {
      setSaving(false);
    }
  };

  const vecSol = (sole?.length ?? 0) > 1;

  return (
    <main className="page">
      <div className="row" style={{ marginBottom: 'var(--s-5)' }}>
        <Logo height={40} sola={sola ?? undefined} />
        <div className="spacer" />
        <button className="btn btn--ghost btn--sm" onClick={onSignOut}>
          <Icon name="left" size={16} /> {t('Nazaj na prijavo')}
        </button>
      </div>

      <div className="card card--brand card--pad row row--top" style={{ marginBottom: 'var(--s-5)' }}>
        <Icon name="award" size={26} className="t-brand" />
        <div>
          <h1 className="t-lg">
            {sola ? t('Dobrodošel v Učilnici šole {sola}', { sola: sola.ime }) : t('Dobrodošel v Učilnici')}
          </h1>
          <p className="t-md t-dim" style={{ marginTop: 2 }}>
            {t('Izpolni podatke — to je potrebno samo enkrat. Številko tečajnika dobiš takoj po shranjevanju.')}
          </p>
        </div>
      </div>

      {/* Kdo si? Tečajnik se vpiše v šolo; inštruktor ustvari svojo. */}
      <div className="tabs" role="tablist" aria-label={t('Kdo si')} style={{ marginBottom: 'var(--s-4)' }}>
        <button className="tab" role="tab" aria-selected={nacin === 'tecajnik'} onClick={() => setNacin('tecajnik')}>
          {t('Sem tečajnik')}
        </button>
        <button className="tab" role="tab" aria-selected={nacin === 'sola'} onClick={() => setNacin('sola')}>
          {t('Sem inštruktor — ustvari šolo')}
        </button>
      </div>

      {nacin === 'sola' && (
        <UstvariSolo session={session} onDone={onDone} onBack={() => setNacin('tecajnik')} />
      )}

      {nacin === 'tecajnik' && (
      <form className="card card--pad stack" onSubmit={submit} noValidate>
        {/* Šola: najprej, ker od nje odvisi vse ostalo. Skrita, če je ena sama. */}
        {sole === null ? (
          <p className="t-sm t-dim" role="status">
            {t('Nalagam …')}
          </p>
        ) : (
          (vecSol || errors.sola) && (
            <Field id="f-sola" label={t('Šola')} required hint={t('Izberi šolo, pri kateri se šolaš.')} error={errors.sola}>
              <select
                id="f-sola"
                className="input"
                value={solaId ?? ''}
                aria-invalid={errors.sola ? 'true' : undefined}
                onChange={(e) => {
                  setSolaId(e.target.value || null);
                  setErrors((x) => ({ ...x, sola: undefined }));
                }}
              >
                <option value="">—</option>
                {sole.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.ime}
                    {s.drzava && s.drzava !== 'SI' ? ` (${s.drzava})` : ''}
                  </option>
                ))}
              </select>
            </Field>
          )
        )}

        {sole !== null && (
          <details>
            <summary className="t-sm t-dim" style={{ cursor: 'pointer' }}>
              {t('Imaš kodo šole?')}
            </summary>
            <div className="row" style={{ marginTop: 'var(--s-2)' }}>
              <input
                className="input"
                aria-label={t('Koda šole')}
                placeholder="npr. PERUT"
                value={koda}
                style={{ textTransform: 'uppercase', maxWidth: 200 }}
                onChange={(e) => setKoda(e.target.value)}
              />
              <button type="button" className="btn btn--secondary" onClick={potrdiKodo} disabled={kodaBusy}>
                {t('Potrdi kodo')}
              </button>
            </div>
          </details>
        )}

        {FIELDS.map((f) => (
          <Field
            key={f.key}
            id={`f-${f.key}`}
            label={t(f.label)}
            required={f.required}
            hint={f.hint && t(f.hint)}
            error={errors[f.key]}
          >
            {f.multi ? (
              <textarea
                id={`f-${f.key}`}
                className="input"
                rows={3}
                value={form[f.key]}
                placeholder={f.placeholder && t(f.placeholder)}
                aria-invalid={errors[f.key] ? 'true' : undefined}
                onChange={(e) => set({ [f.key]: e.target.value })}
              />
            ) : (
              <input
                id={`f-${f.key}`}
                className="input"
                type={f.type ?? 'text'}
                value={form[f.key]}
                placeholder={f.placeholder && t(f.placeholder)}
                autoComplete={f.autoComplete}
                aria-invalid={errors[f.key] ? 'true' : undefined}
                onChange={(e) => set({ [f.key]: e.target.value })}
              />
            )}
          </Field>
        ))}

        {/* Neobvezno in zato brez zvezdice. To je edini podatek, po katerem
            se pozneje vidi, ali se oglaševanje izplača — vsiljena izbira bi
            dala lažen odgovor, ne boljšega. */}
        <Field
          id="f-vir"
          label={t('Kje si izvedel za nas?')}
          hint={t('Neobvezno. Pomaga nam vedeti, kje nas ljudje najdejo.')}
        >
          <select
            id="f-vir"
            className="input"
            value={form.vir_prihoda}
            onChange={(e) => set({ vir_prihoda: e.target.value })}
          >
            <option value="">{t('Raje ne povem')}</option>
            {VIRI.map((v) => (
              <option key={v.key} value={v.key}>
                {t(v.label)}
              </option>
            ))}
          </select>
        </Field>

        <div className="card card--brand card--pad row row--top">
          <input
            id="izjava"
            type="checkbox"
            className="checkbox"
            checked={form.izjava_sprejeta}
            aria-invalid={errors.izjava ? 'true' : undefined}
            onChange={(e) => {
              set({ izjava_sprejeta: e.target.checked });
              setErrors((x) => ({ ...x, izjava: undefined }));
            }}
          />
          <label htmlFor="izjava" className="t-md" style={{ cursor: 'pointer' }}>
            {t('S potrditvijo izjavljam, da se tečaja udeležujem na lastno odgovornost.')}
            {errors.izjava && (
              <span className="field__error" role="alert" style={{ display: 'block' }}>
                {errors.izjava}
              </span>
            )}
          </label>
        </div>

        {/* Politika zasebnosti mora biti dosegljiva PREDEN oddaš podatke,
            ne šele potem. */}
        <p className="t-sm t-dim">
          {t('Podatke uporabimo za vodenje šolanja in prijavo na izpit.')}{' '}
          <button
            type="button"
            className="t-brand t-bold"
            style={{ textDecoration: 'underline', padding: 0 }}
            onClick={() => setZasebnost(true)}
          >
            {t('Kako ravnamo s tvojimi podatki')}
          </button>
        </p>

        <button className="btn btn--primary btn--lg btn--block" type="submit" disabled={saving}>
          {saving ? t('Shranjujem …') : t('Potrdi in začni šolanje')}
          {!saving && <Icon name="right" size={17} />}
        </button>
      </form>
      )}

      {zasebnost && <Zasebnost onClose={() => setZasebnost(false)} />}
    </main>
  );
}

function safeGet(k) {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function safeSet(k, v) {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* ni ključno */
  }
}
